import {
  Avatar,
  Button,
  Menu,
  MenuItem,
  ListItemAvatar,
  ListItemText,
  Typography,
  Divider,
  Box
} from '@coolblue-development/becky';
import { MouseEvent, useState } from 'react';
import { HeaderProps } from './Header';
import LogoutIcon from '../../../assets/logout.svg';
import { useAppContext } from '../../../AppContext';
import { Page } from '../../../models/Page';
import { datadogLogs } from '@datadog/browser-logs';
import { REPORT_IMPROVEMENT_FORM_URL } from '../../constants';
import { useAgentsDashboardAccess } from './useAgentsDashboardAccess';
import { ProfileMenuSkeleton } from './ProfileMenuSkeleton';

const ProfileMenu: React.FC<HeaderProps> = ({ instanceUrl }) => {
  const { setActivePage, agent } = useAppContext();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const agentName = agent?.getName();
  const { isCheckingAccess, isAuthorized, checkAccess } = useAgentsDashboardAccess();

  const handleClick = async (event: MouseEvent<HTMLButtonElement>): Promise<void> => {
    setAnchorEl(event.currentTarget);

    if (agent) {
      const agentArn = agent.getAgentARN();
      await checkAccess(agentArn);
    }
  };

  const handleClose = (): void => {
    setAnchorEl(null);
  };

  const handleCCPPage = (): void => {
    setActivePage(Page.CCP);
    handleClose();

    datadogLogs.logger.info(`CCP panel opened`, { ccpPanel: true });
  };

  const handleSettingsPage = (): void => {
    setActivePage(Page.SETTINGS);
    handleClose();
  };

  const handleAgentsDashboardPage = (): void => {
    setActivePage(Page.AGENTS_DASHBOARD);
    handleClose();
  };

  const handleLogout = (): void => {
    const logoutUrl = `${instanceUrl}/connect/logout`;
    window.location.replace(logoutUrl);
  };

  return (
    <>
      <Button
        sx={{ background: 'transparent', color: 'inherit', font: 'inherit' }}
        preset="secondary"
        onClick={handleClick}
        aria-controls={open ? 'divider-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
      >
        {agentName?.charAt(0)}
      </Button>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{ 'aria-labelledby': 'divider-button' }}
      >
        <MenuItem onClick={handleClose}>
          <ListItemAvatar>
            <Avatar>{agentName?.charAt(0)}</Avatar>
          </ListItemAvatar>
          <ListItemText sx={{ padding: 2 }}>
            <Typography variant="h4">{agentName}</Typography>
            <Typography>Customer Service</Typography>
          </ListItemText>
        </MenuItem>

        <Divider />

        {isCheckingAccess ? (
          <ProfileMenuSkeleton />
        ) : (
          <Box>
            <MenuItem onClick={() => window.open(REPORT_IMPROVEMENT_FORM_URL, '_blank')}>
              <ListItemText>Report Improvement</ListItemText>
            </MenuItem>

            <MenuItem onClick={handleCCPPage}>
              <ListItemText>Old Panel</ListItemText>
            </MenuItem>

            <MenuItem onClick={handleSettingsPage}>
              <ListItemText>Settings</ListItemText>
            </MenuItem>

            {isAuthorized && (
              <MenuItem onClick={handleAgentsDashboardPage}>
                <ListItemText>{Page.AGENTS_DASHBOARD}</ListItemText>
              </MenuItem>
            )}

            <MenuItem onClick={handleLogout}>
              <LogoutIcon />
              <ListItemText>Logout</ListItemText>
            </MenuItem>
          </Box>
        )}
      </Menu>
    </>
  );
};

export default ProfileMenu;
